// src/components/TemplateCard.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, FileText, User, BarChart3, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "./ui/card";

export default function TemplateCard({
                                         template,
                                         showAuthor = false,
                                         showFormsFilled = false,
                                         isAuthenticated,
                                         onLike,
                                         viewHref,
                                         fillHref,
                                     }) {
    const navigate = useNavigate();

    const title = template?.title ?? "Untitled";
    const description = template?.description ?? "";
    const imageUrl = template?.image_url;
    const likeCount = template?.likeCount ?? 0;

    const author = template?.User?.username ?? "Unknown";
    const formsFilled = template?.forms_count ?? 0;

    // Stable gradient placeholder seed from the title
    const seed = (title.charCodeAt(0) || 70) % 3;
    const placeholderGradients = [
        "from-brand-500 to-violet-500",
        "from-indigo-500 to-fuchsia-500",
        "from-violet-600 to-brand-500",
    ];

    return (
        <Card className="group flex flex-col overflow-hidden hover:-translate-y-1 hover:border-brand-300 hover:shadow-card-hover dark:hover:border-brand-700">
            {/* Image / placeholder */}
            <div className="relative h-40 w-full overflow-hidden">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                ) : (
                    <div
                        className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${placeholderGradients[seed]}`}
                    >
                        <FileText className="h-10 w-10 text-white/85" />
                    </div>
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>

            <CardHeader>
                <CardTitle className="line-clamp-1">{title}</CardTitle>
                <CardDescription className="line-clamp-2 min-h-[2.5rem]">
                    {description || "No description provided."}
                </CardDescription>
            </CardHeader>

            <CardContent className="flex-1">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-500 dark:text-zinc-400">
                    {showAuthor && (
                        <span className="inline-flex items-center gap-1.5">
                            <User className="h-4 w-4" />
                            <span className="text-zinc-700 dark:text-zinc-300">{author}</span>
                        </span>
                    )}
                    {showFormsFilled && (
                        <span className="inline-flex items-center gap-1.5">
                            <BarChart3 className="h-4 w-4" />
                            <span className="text-zinc-700 dark:text-zinc-300">{formsFilled}</span> filled
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                        <Heart className="h-4 w-4" />
                        <span className="text-zinc-700 dark:text-zinc-300">{likeCount}</span>
                    </span>
                </div>
            </CardContent>

            <CardFooter className="gap-2">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onLike?.(template.id)}
                    title="Like this template"
                    aria-label="Like template"
                    className="shrink-0 hover:!text-red-500 hover:!border-red-300 hover:!bg-red-50 dark:hover:!bg-red-950/40"
                >
                    <Heart className="h-4 w-4" />
                </Button>

                <Button
                    variant="ghost"
                    className="flex-1"
                    onClick={() => navigate(viewHref)}
                >
                    Details <ArrowUpRight className="h-4 w-4" />
                </Button>

                {isAuthenticated && (
                    <Link to={fillHref} className="flex-1">
                        <Button className="w-full">
                            <FileText className="h-4 w-4" />
                            Fill out
                        </Button>
                    </Link>
                )}
            </CardFooter>
        </Card>
    );
}
